import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { easing } from './lib/easing';

const LIST = 'list';
const GRID = 'grid';

const ACTIVE = 'active';
const INACTIVE = 'inactive';
const SELECTED = 'selected';

const Image = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 12px 24px;
`;

const Wrapper = styled.div`
  --card-status-size: 7px;
  --card-grid-radius: 12px;
  --card-list-radius: 6px;
  --card-grid-radius-with-status: var(--card-status-size)
    var(--card-status-size) var(--card-grid-radius) var(--card-grid-radius);

  background-color: var(--white);
  box-shadow: var(--level-1);
  display: flex;
  transition: ${easing('box-shadow')};

  ${({ onClick }) =>
    onClick &&
    `
      cursor: pointer;

      :hover {
        box-shadow: var(--level-2);
      }
  `}

  ${({ $kind, $status }) => {
    switch ($kind) {
      case GRID:
        return `
          border-radius: ${
            $status
              ? `var(--card-grid-radius-with-status)`
              : `var(--card-grid-radius)`
          };
          flex-direction: column;

          ${Image} {
            background-color: var(--myst);
          }
        `;
      case LIST:
      default:
        return `
          border-radius: var(--card-list-radius);

          & + & {
            margin-top: 16px;
          }

          ${Image} {
            background-color: var(--light-gray);
          }
        `;
    }
  }}

  &::before {
    content: '';
    flex-shrink: 0;
  }

  ${({ $kind, $status, $hasImage }) => {
    switch (true) {
      case $kind === LIST && $status === ACTIVE:
        return `
          &::before {
            background: linear-gradient(0deg, var(--merfolk));
            border-radius: var(--card-list-radius) 0 0 var(--card-list-radius);
            height: auto;
            width: var(--card-status-size);
          }
        `;
      case $kind === LIST && $status === INACTIVE:
        return `border-left: var(--card-status-size) solid var(--status-inactive);`;
      case $kind === LIST && $status === SELECTED:
        return `
          &::before {
            background: linear-gradient(0deg, var(--blurple-haze));
            border-radius: var(--card-list-radius) 0 0 var(--card-list-radius);
            height: auto;
            width: var(--card-status-size);
          }
        `;
      case $kind === LIST && !$status && $hasImage:
        return `border-left: var(--card-status-size) solid var(--light-gray)`;

      case $kind === GRID && $status === ACTIVE:
        return `
          &::before {
            background: linear-gradient(90deg, var(--merfolk));
            border-radius: var(--card-status-size) var(--card-status-size) 0 0;
            height: var(--card-status-size);
            width: auto;
          }
        `;
      case $kind === GRID && $status === INACTIVE:
        return `border-top: var(--card-status-size) solid var(--status-inactive);`;
      case $kind === GRID && $status === SELECTED:
        return `
          &::before {
            background: linear-gradient(90deg, var(--blurple-haze));
            border-radius: var(--card-status-size) var(--card-status-size) 0 0;
            height: var(--card-status-size);
            width: auto;
          }
        `;
      case $kind === GRID && !$status && $hasImage:
        return `border-top: var(--card-status-size) solid var(--myst)`;

      default:
        return ``;
    }
  }}
`;

const Content = styled.div`
  height: 100%;
  min-height: 0;
  min-width: 0;
  padding: 16px 24px;
  width: 100%;
`;

const Card = ({
  children,
  className,
  image,
  kind = LIST,
  onClick,
  status,
  ...props
}) => (
  <Wrapper
    className={className}
    onClick={onClick}
    $kind={kind}
    $status={status}
    $hasImage={!!image}
    {...props}
  >
    {image && <Image>{image}</Image>}
    <Content>{children}</Content>
  </Wrapper>
);

Card.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  image: PropTypes.node,
  kind: PropTypes.oneOf([LIST, GRID]),
  onClick: PropTypes.func,
  status: PropTypes.oneOf([ACTIVE, INACTIVE, SELECTED]),
};

export default styled(Card)``;
